import permission from "@/mixins/permission";

const { get, isEmpty, pull: removeFromArray } = require("lodash");
import store from "@/modules";

const OUT_OF_WARRANTY_PROPERTY = "is_out_of_warranty";

const getEligibilityRules = (isAccessory) => {
  const commonRules = [
    "has_open_customer_requests",
    "fraud_indicator",
    "is_product_unavailable_for_request",
  ];

  const refundRules = ["is_refurbished"];

  if (!isAccessory) {
    commonRules.push("is_deleted_or_deactivated");
    commonRules.push("is_shared_user");
    commonRules.push("market_unavailable");
    commonRules.push("is_out_of_warranty");
    // Refund
    refundRules.push("is_3p_retailer");
  }

  return {
    // Common rules that apply to all types
    common: commonRules,
    // Specific rules for each type
    refund: refundRules,
    replacement: [],
    parts: [],
  };
};
export const Message = (rule, action) => {
  const messages = {
    plc_coverage_information_missing:
      "Cannot retrieve eligibility and warranty info. Please try again later.",
    is_deleted_or_deactivated: "Device is deleted/deactivated",
    is_out_of_warranty: "Device is out of warranty.",
    has_open_customer_requests: `A replacement or refund request is already in progress for this device`,
    is_refurbished: "Device is a Certified Refurbished product",
    is_product_unavailable_for_request: `This model is not eligible for ${action}`,
    fraud_indicator: "Account has been flagged for suspicious activity",
    is_shared_user:
      "Only the primary device owner can request this action. Shared users are not authorized.",
    market_unavailable:
      "Service not available in the device's current operating location",
    is_3p_retailer:
      "Refunds are not available for products purchased from third party retailers",
  };
  return messages[rule] || "";
};

export class PermissionChecker {
  constructor(store) {
    this.store = store;
  }

  get isSupervisor() {
    return this.store.getters["auth/isSupervisor"];
  }

  checkPermission(params) {
    return this.store.getters["shared/userHasPermission"](params);
  }
}

// Create a Null Coverage class
export class NullDeviceWarrantyAndEligibilityPolicy {
  constructor() {
    this.isNullObject = true;
    this.doorbotId = null;
    this.macAddress = null;
    this.isAccessory = false;
    this.isInWarranty = false;
    this.warrantyNotAvailable = true;
    this.isThirdPartyDevice = false;
    this.services = ["replacement", "refund", "parts"];

    // Add the new delayed fulfillment properties with default values
    this.isDelayedFulfillment = false;
    this.delayedFulfillmentReason = "";

    // Create null service eligibility objects with only the missing information rule
    const nullRules = { plc_coverage_information_missing: true };
    this.refund = new ServiceEligibility(nullRules, "refund");
    this.replacement = new ServiceEligibility(nullRules, "replacement");
    this.parts = new ServiceEligibility(nullRules, "parts");

    // Create empty warranty
    this.warranty = new Warranty({});
    this.eligibleServices = [];
    this.isEligible = false;
  }
}

export class EligibilityRule {
  constructor(value = true, text) {
    this._value = value;
    this._text = text;
  }
  get isRestricting() {
    return this._value;
  }
  get text() {
    return this._text;
  }
}

export class ServiceEligibility {
  constructor(rules = {}, type, isAccessory = false) {
    this.type = type;
    this.isAccessory = isAccessory;
    this.rules = this._createRules(rules);
    this.permissionChecker = new PermissionChecker(store);
    this.isSupervisor = this.permissionChecker.isSupervisor;
    const check = (permission, resource) => {
      return this.permissionChecker.checkPermission({
        permission: permission,
        on: resource,
      });
    };
    this.isUserAllowedOOWRefund = check("create", "OutOfWarrantyRefund");
    this.isUserAllowedOOWReplace = check("create", "OutOfWarrantyReplacement");
    this.isUserAllowedOOWSendParts = check("create", "OutOfWarrantyParts");
    this.isUserAllowedToRefund = check("create", "RefundRequest");
    this.isUserAllowedToReplace = check("create", "ReplacementRequest");
    this.isUserAllowedToSendParts = check("create", "PartsRequest");
  }

  _createRules(rules) {
    // This first case handles the null pattern implementation
    // When PLC coverage information is missing (plc_coverage_information_missing = true),
    // we return only a single rule to indicate missing coverage info
    // This is used by the NullDeviceWarrantyAndEligibilityPolicy class to handle cases with no data
    let applicableRules,
      missingInfoKey = "plc_coverage_information_missing";
    if (get(rules, missingInfoKey, false) === true) {
      // For handle null eligibility/warranty cases.
      applicableRules = [missingInfoKey];
    } else {
      const rules = getEligibilityRules(this.isAccessory);
      applicableRules = rules.common.concat(rules[this.type]);
    }

    const eligibilityRules = {};
    applicableRules.forEach((key) => {
      let value = get(rules, key, true);

      eligibilityRules[key] = new EligibilityRule(
        value,
        Message(key, this.type)
      );
    });

    return eligibilityRules;
  }

  _loopRules(callback) {
    const result = [];
    Object.entries(this.rules).forEach(([prop, rule]) => {
      if (rule.isRestricting) {
        result.push(callback(prop, rule));
      }
    });

    return result;
  }

  get ineligibilityReasons() {
    return this._loopRules((_, rule) => rule.text);
  }

  get ineligibleRules() {
    return this._loopRules((prop) => prop);
  }

  get nonBypassableRestrictions() {
    const nonBypassableList = ["has_open_customer_requests", "fraud_indicator"];

    return this.ineligibleRules
      .filter((rule) => nonBypassableList.includes(rule))
      .map((rule) => Message(rule, this.type));
  }

  get noBypassable() {
    return this.nonBypassableRestrictions.length > 0;
  }

  get eligibilityBypassed() {
    return this.isEligible && this.ineligibleRules.length > 0;
  }

  get isEligible() {
    return this.ineligibleRules.length === 0;
  }
  get isRestricted() {
    return !this.isEligible;
  }

  get isUserAllowed() {
    switch (this.type) {
      case "refund":
        return this.isRestricted
          ? this.isUserAllowedOOWRefund
          : this.isUserAllowedToRefund;
      case "replacement":
        return this.isRestricted
          ? this.isUserAllowedOOWReplace
          : this.isUserAllowedToReplace;
      case "parts":
        return this.isRestricted
          ? this.isUserAllowedOOWSendParts
          : this.isUserAllowedToSendParts;
      default:
        return false;
    }
  }
}

export class Warranty {
  constructor(warrantyData = {}) {
    this.inWarranty = get(warrantyData, "in_warranty", false);
    this.notes = warrantyData.notes || "Warranty Status Not Available";
    this.code = warrantyData.code || "ND-001";
    this.standardExpiration = warrantyData.standard_expiration || "N/A";
    this.activationDate = warrantyData.activation_date || "N/A";
    this.extendedWarranty = get(warrantyData, "extended_warranty", false);
    this.warehouseDeal = get(warrantyData, "warehouse_deal", false);
    this.status = warrantyData.status || "Not Available";
  }

  toJson() {
    return {
      in_warranty: this.inWarranty,
      notes: this.notes,
      code: this.code,
      standard_expiration: this.standardExpiration,
      activation_date: this.activationDate,
      extended_warranty: this.extendedWarranty,
      warehouse_deal: this.warehouseDeal,
      status: this.status,
    };
  }
}

// Main Class
export class DeviceWarrantyAndEligibilityPolicy {
  constructor(device, isAccessory) {
    if (isEmpty(device?.eligibility)) {
      throw new Error("Eligibility and Warranty information required!");
    }
    this.isNullObject = false;

    const eligibility = device?.eligibility || {};
    const warrantyData = device?.warranty || {};
    const {
      refund_rules: refund = {},
      replacement_rules: replacement = {},
      parts_rules: parts = {},
    } = eligibility?.rules || {};

    this.permissionChecker = new PermissionChecker(store);
    this.checkPermission = this.permissionChecker.checkPermission.bind(
      this.permissionChecker
    );
    this.isSupervisor = this.permissionChecker.isSupervisor;

    this.doorbotId = device.doorbot_id;
    this.macAddress = device.mac_address;
    this.isAccessory = isAccessory || false;

    this.isInWarranty = warrantyData?.in_warranty || false;
    this.warrantyNotAvailable =
      warrantyData?.warranty?.status?.toLowerCase() === "not available";

    // Add the new delayed fulfillment properties
    this.isDelayedFulfillment = get(
      eligibility,
      "is_delayed_fulfillment",
      false
    );
    this.delayedFulfillmentReason = get(
      eligibility,
      "delayed_fulfillment_reason",
      ""
    );

    // Composition
    this.warranty = new Warranty(warrantyData);

    this.refund = new ServiceEligibility(refund, "refund", isAccessory);
    this.replacement = new ServiceEligibility(
      replacement,
      "replacement",
      isAccessory
    );
    this.parts = new ServiceEligibility(parts, "parts", isAccessory);

    this.services = ["replacement", "refund", "parts"];
  }

  get eligibleServices() {
    const services = [];
    if (this.refund.isEligible) services.push("Refund");
    if (this.replacement.isEligible) services.push("Replacement");
    if (this.parts.isEligible) services.push("Parts");

    return services;
  }

  get isEligible() {
    return this.eligibleServices.length === 0;
  }
}

// Factory method
DeviceWarrantyAndEligibilityPolicy.create = function (
  device,
  isAccessory = false
) {
  return isEmpty(device?.eligibility)
    ? new NullDeviceWarrantyAndEligibilityPolicy()
    : new DeviceWarrantyAndEligibilityPolicy(device, isAccessory);
};

// export { DeviceWarrantyAndEligibilityPolicy };
